import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Alert, Button, Grid2, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { FormField, FormFieldType } from '@enroller/admissions-components';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { usePageTitle } from '@enroller/utils';
import { yesNoOptions } from '../../../constants/yesNoOptions.ts';
import { adultDefaultValues, defaultValues, FamilyOverviewFormValues, familyOverviewSchema } from './FamilyOverviewFormValues.ts';
import { genderOptions } from '../../../constants/gender.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { getConfig } from '../../../utils/config.ts';

const educationLevelOptions = [
  { label: 'High School', value: 'High School' },
  { label: 'College', value: 'College' },
  { label: 'Graduate School', value: 'Graduate School' },
  { label: 'Other', value: 'Other' },
];

const livingAtHomeOptions = [
  { label: 'Full-Time', value: 'full-time' },
  { label: 'Part-Time', value: 'part-time' },
];

const relationshipOptions = [{ value: 'Father' }, { value: 'Mother' }, { value: 'Sibling' }, { value: 'Other' }];

const interestsActivitiesGroupedOptions = [
  {
    group: 'Sports',
    options: [
      { label: 'Swimming', value: 'Swimming' },
      { label: 'Volleyball', value: 'Volleyball' },
      { label: 'Running / Track', value: 'Running / Track' },
    ],
  },
  {
    group: 'The Arts',
    options: [
      { label: 'Calligraphy', value: 'Calligraphy' },
      { label: 'Photography', value: 'Photography' },
      { label: 'Singing', value: 'Singing' },
    ],
  },
  {
    group: 'Other Hobbies & Interests',
    options: [
      { label: 'Baking', value: 'Baking' },
      { label: 'Cooking', value: 'Cooking' },
      { label: 'Gardening', value: 'Gardening' },
      { label: 'Reading', value: 'Reading' },
      { label: 'Movies', value: 'Movies' },
      { label: 'Visiting Museums', value: 'Visiting Museums' },
    ],
  },
];

const expectedGraduationDate = [{ value: '2024' }, { value: '2025' }, { value: '2026' }, { value: '2027' }];

const languageOptions = [{ value: 'English' }, { value: 'Other' }];

const { googleMapsAPIKey } = getConfig();

export const FamilyOverviewForm = () => {
  return (
    <PageContainer>
      <FamilyOverviewSection />
      <ChildrenSection />
      <ContactInformationSection />
      <PhotoSection />
    </PageContainer>
  );
};

const FamilyOverviewSection = () => {
  const { control } = useFormContext<FamilyOverviewFormValues>();
  const adultBlockDisabled = useWatch({ control, name: 'overview.singleAdultConfirm' });

  const { fields: adults, append: addAdult } = useFieldArray({
    control,
    name: 'overview.adults',
  });

  const handleAddAdult = () => {
    addAdult(adultDefaultValues);
  };

  return (
    <PaperSection title="Family Overview">
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">Please add all adults living in the home including yourself.</Typography>
        </Alert>
      </Grid2>

      <Grid2 size={4}>
        <FormField
          control={control}
          label="What is the primary language spoken in your household?"
          type={FormFieldType.Dropdown}
          options={languageOptions}
          name={`overview.languageInHousehold`}
        />
      </Grid2>

      <Grid2 size={12}>
        <Typography variant="subtitle1" fontWeight={600}>
          Adults
        </Typography>
      </Grid2>

      <Grid2 size={12}>
        <FormField control={control} name="overview.singleAdultConfirm" type={FormFieldType.Checkbox} label="There are no adults living in this household" />
      </Grid2>

      {adults.map((adult, adultIndex) => (
        <AdultBlock key={adult.id} index={adultIndex} disabled={adultBlockDisabled} />
      ))}

      <Grid2 container>
        <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddAdult} disabled={adultBlockDisabled}>
          Add Another Adult
        </Button>
      </Grid2>
    </PaperSection>
  );
};

interface AdultBlockProps {
  index: number;
  disabled: boolean;
}

const AdultBlock = ({ index, disabled }: AdultBlockProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FamilyOverviewFormValues>();

  return (
    <Paper sx={{ p: 2 }}>
      <Grid2 container spacing={3}>
        <Grid2 size={4}>
          <FormField control={control} disabled={disabled} label="First Name" type={FormFieldType.Text} name={`overview.adults.${index}.firstName`} />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} disabled={disabled} label="Last Name" type={FormFieldType.Text} name={`overview.adults.${index}.lastName`} />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} disabled={disabled} label="Date of Birth" type={FormFieldType.Date} name={`overview.adults.${index}.dateOfBirth`} />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Gender"
            type={FormFieldType.Radio}
            options={genderOptions}
            name={`overview.adults.${index}.gender`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Relationship"
            type={FormFieldType.Dropdown}
            options={relationshipOptions}
            name={`overview.adults.${index}.relationship`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Is this is a Host Parent"
            options={yesNoOptions}
            type={FormFieldType.Radio}
            name={`overview.adults.${index}.isThisHostParent`}
          />
        </Grid2>
        <Grid2 container size={12}>
          <Grid2 size={7}>
            <FormField
              control={control}
              disabled={disabled}
              label="Interests/ Activities"
              type={FormFieldType.TagsAutocomplete}
              name={`overview.adults.${index}.interestsActivities`}
              groupedOptions={interestsActivitiesGroupedOptions}
            />
          </Grid2>
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="What is your native language?"
            name={`overview.adults.${index}.nativeLanguage`}
            options={languageOptions}
            type={FormFieldType.Dropdown}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Education Level"
            name={`overview.adults.${index}.educationLevel`}
            options={educationLevelOptions}
            type={FormFieldType.Dropdown}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Community Involvement"
            type={FormFieldType.Text}
            name={`overview.adults.${index}.communityInvolvement`}
          />
        </Grid2>
        <Grid2 size={5}>
          <FormField
            control={control}
            disabled={disabled}
            label="Living at Home"
            type={FormFieldType.Radio}
            options={livingAtHomeOptions}
            name={`overview.adults.${index}.livingAtHome`}
          />
        </Grid2>
        <Grid2 size={7}>
          <FormField
            control={control}
            disabled={disabled}
            label="If living in the home part-time, please share detailed explanation of the time in the home"
            type={FormFieldType.Text}
            name={`overview.adults.${index}.partTimeExplanation`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Cell Phone Number"
            type={FormFieldType.Text}
            name={`overview.adults.${index}.cellPhoneNumber`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Work Phone Number"
            type={FormFieldType.Text}
            name={`overview.adults.${index}.workPhoneNumber`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} disabled={disabled} label="Email Address" type={FormFieldType.Text} name={`overview.adults.${index}.emailAddress`} />
        </Grid2>
        <Grid2 container size={12}>
          <Grid2 size={4}>
            <FormField
              control={control}
              disabled={disabled}
              label="Social Media Profile Link"
              type={FormFieldType.Text}
              name={`overview.adults.${index}.socialMediaProfileLink`}
              helperText={!errors.overview?.adults?.[index]?.socialMediaProfileLink ? 'Please enter one social media link you can be contacted on' : ''}
            />
          </Grid2>
        </Grid2>
        <Grid2 size={4}>
          <FormField disabled={disabled} control={control} label="Employer" type={FormFieldType.Text} name={`overview.adults.${index}.employer`} />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} disabled={disabled} label="Job Title" type={FormFieldType.Text} name={`overview.adults.${index}.jobTitle`} />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Work Schedule"
            type={FormFieldType.Text}
            name={`overview.adults.${index}.workSchedule`}
            helperText={!errors.overview?.adults?.[index]?.workSchedule ? 'How many days per week and hours per day approximately' : ''}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Job Contact Name"
            type={FormFieldType.Text}
            name={`overview.adults.${index}.jobContactName`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Job Contact Phone Number"
            type={FormFieldType.Text}
            name={`overview.adults.${index}.jobContactPhoneNumber`}
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            id="TODO_API_PHOTO_ID"
            control={control}
            disabled={disabled}
            label="Photo (optional)"
            type={FormFieldType.PictureUploader}
            name={`overview.adults.${index}.photo`}
          />
        </Grid2>
      </Grid2>
    </Paper>
  );
};

const ChildrenSection = () => {
  const { control } = useFormContext<FamilyOverviewFormValues>();

  const isNoChildren = useWatch({ control, name: 'children.noChildrenAtHome' });

  const { fields: children, append: addChildren } = useFieldArray({
    control,
    name: 'children.childrenList',
  });

  const handleAddChildFrom = () => {
    addChildren(adultDefaultValues);
  };

  return (
    <PaperSection title="Children">
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">
            Please list all children, including any who may be in college or not living in the home. If there are no children, please check the box below.
          </Typography>
        </Alert>
      </Grid2>

      <Grid2 size={12}>
        <FormField control={control} name="children.noChildrenAtHome" type={FormFieldType.Checkbox} label="There are no children living in this household" />
      </Grid2>

      {children.map((child, childIndex) => (
        <ChildBlock key={child.id} index={childIndex} disabled={isNoChildren} />
      ))}

      <Grid2 size={12}>
        <Button disabled={isNoChildren} startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddChildFrom}>
          Add Another Child
        </Button>
      </Grid2>
    </PaperSection>
  );
};

interface ChildBlockProps {
  index: number;
  disabled: boolean;
}

const ChildBlock = ({ index, disabled }: ChildBlockProps) => {
  const { control } = useFormContext<FamilyOverviewFormValues>();

  return (
    <Paper sx={{ p: 2 }}>
      <Grid2 container spacing={3}>
        <Grid2 size={4}>
          <FormField control={control} disabled={disabled} label="First Name" type={FormFieldType.Text} name={`children.childrenList.${index}.firstName`} />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} disabled={disabled} label="Last Name" type={FormFieldType.Text} name={`children.childrenList.${index}.lastName`} />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Date of Birth"
            type={FormFieldType.Date}
            name={`children.childrenList.${index}.dateOfBirth`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Gender"
            type={FormFieldType.Radio}
            options={genderOptions}
            name={`children.childrenList.${index}.gender`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Expected Graduation Date"
            type={FormFieldType.Dropdown}
            options={expectedGraduationDate}
            name={`children.childrenList.${index}.expectedGraduationDate`}
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            disabled={disabled}
            label="Community Involvement"
            type={FormFieldType.Text}
            name={`children.childrenList.${index}.communityInvolvement`}
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            disabled={disabled}
            label="Living at Home"
            type={FormFieldType.Radio}
            options={livingAtHomeOptions}
            name={`children.childrenList.${index}.livingAtHome`}
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            disabled={disabled}
            label="If living in the home part-time, please share detailed explanation of the time in the home"
            type={FormFieldType.Text}
            name={`children.childrenList.${index}.partTimeExplanation`}
          />
        </Grid2>
        <Grid2 size={7}>
          <FormField
            control={control}
            disabled={disabled}
            label="Interests/ Activities"
            type={FormFieldType.TagsAutocomplete}
            name={`children.childrenList.${index}.interestsActivities`}
            groupedOptions={interestsActivitiesGroupedOptions}
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            id="TODO_API_CHILD_PHOTO_ID"
            control={control}
            disabled={disabled}
            label="Photo (optional)"
            type={FormFieldType.PictureUploader}
            name={`children.childrenList.${index}.photo`}
          />
        </Grid2>
      </Grid2>
    </Paper>
  );
};

const ContactInformationSection = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FamilyOverviewFormValues>();
  const isMailingSameWatch = useWatch({ control, name: 'contactInformation.isMailingSame' });

  return (
    <PaperSection title="Contact Information">
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">P.O. Boxes are not acceptable for physical address.</Typography>
        </Alert>
      </Grid2>

      <Grid2 size={12}>
        <FormField
          control={control}
          label="Physical Address"
          type={FormFieldType.AddressAutocomplete}
          name="contactInformation.physicalAddress"
          googleMapsAPIKey={googleMapsAPIKey}
        />
      </Grid2>
      <Grid2 size={12}>
        <FormField
          control={control}
          label="Mailing address is the same as physical address"
          type={FormFieldType.Checkbox}
          name="contactInformation.isMailingSame"
        />
      </Grid2>

      {!isMailingSameWatch && (
        <Grid2 size={12}>
          <FormField
            control={control}
            label="Mailing Address"
            type={FormFieldType.AddressAutocomplete}
            name="contactInformation.mailingAddress"
            googleMapsAPIKey={googleMapsAPIKey}
          />
        </Grid2>
      )}
      <Grid2 container size={12}>
        <Grid2 size={4}>
          <FormField
            control={control}
            label="Nearest Airport"
            type={FormFieldType.Text} // TODO: Change to Dropdown
            name="contactInformation.nearestAirport"
          />
        </Grid2>
        <Grid2 size={4} container direction="column" spacing={1} pt={0.5}>
          <Typography variant="caption" component="p">
            Distance to Airport
          </Typography>
          <Typography variant="body2">34.8mi (calculated)</Typography>
        </Grid2>
      </Grid2>
      <Grid2 size={4}>
        <FormField
          control={control}
          label="Emergency Contact Person"
          type={FormFieldType.Text}
          name="contactInformation.emergencyContactPerson"
          helperText={
            !errors.contactInformation?.emergencyContactPerson ? 'Note: Other responsible adult not living in your home and not your local coordinator' : ''
          }
        />
      </Grid2>
      <Grid2 size={4}>
        <FormField control={control} label="Emergency Phone Number" type={FormFieldType.Text} name="contactInformation.emergencyPhoneNumber" />
      </Grid2>
    </PaperSection>
  );
};

const PhotoSection = () => {
  const { control } = useFormContext<FamilyOverviewFormValues>();

  return (
    <PaperSection title="Photos">
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">
            Your family photo is the first image that a prospective student will see and will be displayed over there on the left of this page. This photo
            should include all of your family members in it.
          </Typography>
        </Alert>
      </Grid2>

      <Grid2 size={12}>
        <FormField id="TODO_API_FAMILY_PHOTO_ID" control={control} label="Family Photo" name="familyPhoto" type={FormFieldType.PictureUploader} />
      </Grid2>
    </PaperSection>
  );
};

const PageContainer = ({ children }: { children: ReactNode }) => {
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? familyOverviewSchema.required : familyOverviewSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  useEffect(() => {
    setHeaderTitle({ title: 'Overview', subtitle: 'Family Overview' });
    setPageTitle('Overview');
  }, [setHeaderTitle, setPageTitle]);

  const formMethods = useForm<FamilyOverviewFormValues>({
    defaultValues,
    resolver,
  });

  const { handleSubmit } = formMethods;

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        console.log('Form submitted:', data);
      })(),
    [handleSubmit],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </Stack>
  );
};

type PaperSectionProps = {
  title: string;
  pageTitle?: string;
  children: ReactNode;
};

const PaperSection = ({ title, pageTitle, children }: PaperSectionProps) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Grid2 container spacing={3}>
        {pageTitle && (
          <Grid2 size={12}>
            <Typography variant="subtitle1" fontWeight={600}>
              {pageTitle}
            </Typography>
          </Grid2>
        )}
        <Grid2 size={12}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        </Grid2>
        {children}
      </Grid2>
    </Paper>
  );
};
